import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL } from "@/lib/format";
import { RepriceDialog } from "@/pages/LotDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, FileSpreadsheet } from "lucide-react";
import ImportItemsDialog from "@/components/ImportItemsDialog";

export default function Items() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lotFilter, setLotFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, l] = await Promise.all([api.get("/items"), api.get("/lots")]);
      setItems(i.data);
      setLots(l.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const filtered = items.filter((it) => {
    if (lotFilter !== "ALL" && it.lot_id !== lotFilter) return false;
    if (statusFilter !== "ALL" && it.status !== statusFilter) return false;
    if (q && !`${it.internal_code} ${it.description} ${it.brand || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Catálogo de Mercadorias</h1>
          <p className="text-muted-foreground mt-1">Peças únicas e itens vinculados aos lotes</p>
        </div>
        <ImportItemsDialog lots={lots} onDone={load} trigger={<Button variant="outline" data-testid="import-items-open"><FileSpreadsheet className="h-4 w-4 mr-2" /> Importar</Button>} />
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar por código, descrição ou marca" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mercadorias-search" />
        </div>
        <Select value={lotFilter} onValueChange={setLotFilter}>
          <SelectTrigger className="w-[170px]" data-testid="mercadorias-lot-filter"><SelectValue placeholder="Lote" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os lotes</SelectItem>
            {lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="mercadorias-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="DISPONIVEL">Disponível</SelectItem>
            <SelectItem value="VENDIDO">Vendido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground">Nenhuma mercadoria. Cadastre itens dentro de um lote.</p>
        ) : (
          <Table data-testid="mercadorias-table">
            <TableHeader><TableRow>
              <TableHead>Código</TableHead><TableHead>Descrição</TableHead><TableHead>Lote</TableHead><TableHead>Categoria</TableHead>
              <TableHead className="text-right">Inicial</TableHead><TableHead className="text-right">Vigente</TableHead>
              <TableHead className="text-right">Disp.</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="font-num font-semibold">{it.internal_code}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{it.description}</TableCell>
                  <TableCell className="font-num text-accent cursor-pointer hover:underline" onClick={() => navigate(`/lotes/${it.lot_id}`)}>{it.lot_code}</TableCell>
                  <TableCell>{it.category || "—"}</TableCell>
                  <TableCell className="text-right font-num">{formatBRL(it.initial_price)}</TableCell>
                  <TableCell className="text-right font-num font-semibold">{formatBRL(it.current_price)}</TableCell>
                  <TableCell className="text-right font-num">{it.qty_available}/{it.qty_received}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded font-medium ${it.status === "VENDIDO" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{it.status === "VENDIDO" ? "Vendido" : "Disponível"}</span></TableCell>
                  <TableCell>
                    {it.qty_available > 0 && <RepriceDialog item={it} onDone={load} trigger={<Button size="sm" variant="outline" data-testid={`mercadoria-remarcacao-button-${it.internal_code}`}>Remarcar</Button>} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
